import React, { useState, useEffect } from "react";
import { fetchDriftAnalysis, fetchTrendAnalytics } from "../../../lib/researchApi";
import TrendIndicator from "../shared/TrendIndicator";

const SUPPORTED_STRATEGIES = ["wheel", "earnings", "tactical_spread", "regime_spread"];
const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

function DriftBar({ label, value, maxValue = 1, warn }) {
    const pct = maxValue > 0 ? Math.min(value / maxValue, 1) : 0;
    const color = warn ? "#f87171" : value > 0.15 ? "#fbbf24" : "#4ade80";
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: 4 }}>
                <span style={{ color: "var(--savvy-muted)" }}>{label}</span>
                <span style={{ color, fontWeight: 600 }}>{value != null ? value.toFixed(4) : "—"}</span>
            </div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2 }}>
                <div style={{
                    height: "100%",
                    width: `${pct * 100}%`,
                    background: color,
                    borderRadius: 2,
                    transition: "width 0.4s ease",
                }} />
            </div>
        </div>
    );
}

export default function DriftSection() {
    const [strategy, setStrategy] = useState(SUPPORTED_STRATEGIES[0]);
    const [driftData, setDriftData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([fetchDriftAnalysis(strategy), fetchTrendAnalytics(strategy)])
            .then(([drift, trends]) => {
                setDriftData(drift);
                setTrendData(trends);
            })
            .catch((e) => setError(e?.response?.data?.error || "Failed to load drift data"))
            .finally(() => setLoading(false));
    }, [strategy]);

    const paramDrift = driftData?.parameter_drift || {};
    const valDrift = driftData?.validation_drift || {};
    const ciWidths = driftData?.ci_width_trend || [];
    const driftWarnings = driftData?.warnings || [];

    const trendMetrics = trendData?.metrics || {};

    return (
        <div>
            {/* Strategy selector */}
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {SUPPORTED_STRATEGIES.map(s => (
                    <button
                        key={s}
                        onClick={() => setStrategy(s)}
                        style={{
                            padding: "5px 12px",
                            borderRadius: 6,
                            fontSize: "12px",
                            fontWeight: s === strategy ? 700 : 500,
                            background: s === strategy ? "rgba(159,234,232,0.12)" : "transparent",
                            border: s === strategy ? "1px solid rgba(159,234,232,0.4)" : "1px solid rgba(255,255,255,0.1)",
                            color: s === strategy ? "var(--savvy-accent)" : "var(--savvy-muted)",
                            cursor: "pointer",
                        }}
                    >
                        {STRATEGY_LABELS[s]}
                    </button>
                ))}
            </div>

            {/* Drift warnings */}
            {driftWarnings.length > 0 && (
                <div style={{
                    padding: "8px 12px",
                    background: "rgba(248,113,113,0.08)",
                    border: "1px solid rgba(248,113,113,0.3)",
                    borderRadius: 8,
                    marginBottom: 16,
                }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "#f87171", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                        Drift Warnings
                    </div>
                    {driftWarnings.map((w, i) => (
                        <div key={i} style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)", marginBottom: 2 }}>⚠ {w}</div>
                    ))}
                </div>
            )}

            {loading && <div style={{ color: "var(--savvy-muted)", fontSize: "13px", padding: "24px 0", textAlign: "center" }}>Loading drift analysis…</div>}
            {error && <div style={{ color: "#f87171", fontSize: "13px" }}>Error: {error}</div>}

            {!loading && !error && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
                    {/* Parameter drift */}
                    <div style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 10, padding: "16px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                            Parameter Drift
                        </div>
                        {Object.entries(paramDrift).length > 0
                            ? Object.entries(paramDrift).map(([key, val]) => (
                                <DriftBar
                                    key={key}
                                    label={key.replace(/_/g, " ")}
                                    value={typeof val === "number" ? val : val?.drift ?? 0}
                                    warn={typeof val === "number" ? val > 0.2 : val?.drift > 0.2}
                                />
                            ))
                            : <div style={{ fontSize: "12px", color: "var(--savvy-dim)" }}>No parameter drift data</div>
                        }
                    </div>

                    {/* Validation drift */}
                    <div style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 10, padding: "16px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                            Validation Drift
                        </div>
                        {Object.entries(valDrift).length > 0
                            ? Object.entries(valDrift).map(([key, val]) => (
                                <DriftBar
                                    key={key}
                                    label={key.replace(/_/g, " ")}
                                    value={typeof val === "number" ? val : val?.drift ?? 0}
                                    warn={typeof val === "number" ? val > 0.15 : val?.drift > 0.15}
                                />
                            ))
                            : <div style={{ fontSize: "12px", color: "var(--savvy-dim)" }}>No validation drift data</div>
                        }
                    </div>

                    {/* CI width trend + metric trends */}
                    <div style={{ background: "var(--savvy-panel)", border: "1px solid var(--savvy-border)", borderRadius: 10, padding: "16px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                            Metric Trend Directions
                        </div>
                        {Object.entries(trendMetrics).length > 0
                            ? Object.entries(trendMetrics).map(([key, meta]) => (
                                <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                    <div>
                                        <div style={{ fontSize: "11px", color: "var(--savvy-text)" }}>
                                            {key.split(".").pop()?.replace(/_/g, " ")}
                                        </div>
                                        <div style={{ fontSize: "10px", color: "var(--savvy-dim)" }}>
                                            slope: {meta.slope != null ? meta.slope.toFixed(4) : "—"}
                                        </div>
                                    </div>
                                    <TrendIndicator direction={meta.direction} showLabel />
                                </div>
                            ))
                            : (
                                ciWidths.length > 0
                                    ? ciWidths.map((w, i) => (
                                        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: 6 }}>
                                            <span style={{ color: "var(--savvy-muted)" }}>
                                                {w.timestamp ? new Date(w.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : `Run ${i + 1}`}
                                            </span>
                                            <span style={{ color: w.width > 0.3 ? "#f87171" : "var(--savvy-text)", fontWeight: 600 }}>
                                                {w.width != null ? w.width.toFixed(3) : "—"}
                                            </span>
                                        </div>
                                    ))
                                    : <div style={{ fontSize: "12px", color: "var(--savvy-dim)" }}>No trend data</div>
                            )
                        }
                    </div>
                </div>
            )}
        </div>
    );
}
